import { Form, FormikHelpers, useFormikContext } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppDispatch } from '../../store';
import { validations } from '../../utils/validations';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { SectionHeader } from '../common/SectionHeader';
import { visitationListActions } from '../../store/entities/visitationList';
import { Box, Typography } from '@mui/material';
import { faPersonWalkingArrowRight } from '@fortawesome/free-solid-svg-icons';
import { SupplierAutocomplete } from '../common/form/SupplierAutocomplete';
import { SupplierEmployeeAutocomplete } from '../common/form/SupplierEmployeeAutocomplete';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { QcsDivider } from '../common/basic/QcsDivider';
import { Select, SelectItem } from '../common/form/Select';
import { FaIcon } from '../common/FaIcon';
import { getLangNameObj } from '../../utils/format';
import { invitationTypeVisitListActions } from '../../store/entities/invitationTypeVisitList';
import { EstablishmentBasic } from '../common/form/Establishment';
import { ShowPinBtn } from '../supplier/ShowPinBtn';
import { SupplierDto } from '@api/models/suppliers/SupplierDto';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { QcsButton } from '../common/basic/QcsButton';
import { VisitationModeSelector } from './VisitationModeSelector';
import { UserState } from '@api/models/enums/UserState';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { supplierEmployeeService, visitationService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { apiMessagesStore } from '@api/utils/ApiMessagesStore';
import { resources } from '@api/resources';

interface VisitationAddPinForm {
  mode: 'walk' | 'car';
  type: 'pin' | 'person';
  invitationId: string;
  spz?: string;
  pin?: string;
  supplier?: SupplierDto;
  supplierEmployee?: SupplierEmployeeDto;
  establishment?: string;
  note?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onOneTimeClick: () => void;
}

interface VisitationFormContextType {
  hasValidCar: boolean;
  setHasValidCar: (value: boolean) => void;
  validSuppliers: SupplierDto[] | null;
  setValidSuppliers: (value: SupplierDto[] | null) => void;
}

export const VisitationFormContext = createContext<VisitationFormContextType | undefined>(undefined);

export const useVisitationFormContext = () => {
  const context = useContext(VisitationFormContext);
  if (!context) {
    throw new Error('useVisitationFormContext must be used within VisitationFormContext.Provider');
  }
  return context;
};

const VisitationFormContent: FC<{ invitationList: InvitationDto[] }> = ({ invitationList }) => {
  const { t, i18n } = useTranslation();
  const { values } = useFormikContext<VisitationAddPinForm>();
  const { hasValidCar, validSuppliers } = useVisitationFormContext();

  return (
    <Form>
      <FormContainer>
        <Box sx={{ minHeight: '480px' }}>
          <EstablishmentBasic name="establishment" label={t('workplace.establishment')} required fullWidth />
          <VisitationModeSelector />
          {invitationList.length === 0 ? (
            <>
              {/* Identification section */}
              <Box
                sx={{
                  mt: values.mode === 'car' ? 2 : 2,
                  pt: values.mode === 'car' ? 2 : 0,
                  borderTop: values.mode === 'car' ? '1px solid #eee' : 'none',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12, mb: 1, display: 'block' }}
                >
                  {values.mode === 'car' ? t('visitation.driverIdentificationStep') : t('visitation.personIdentification')}
                </Typography>
                <Input type="number" name="pin" label={t('visitation.pin')} fullWidth disabled={hasValidCar} autoComplete="off" />

                <QcsDivider text={t('visitation.orManually')} />

                {/* Manual entry */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <SupplierAutocomplete name="supplier" label={t('visitation.supplier')} allowVisit={true} filterSuppliers={validSuppliers} />

                  <Box sx={{ mt: 1.5 }}>
                    <SupplierEmployeeAutocomplete
                      style={{ width: '100%' }}
                      name="supplierEmployee"
                      label={t('visitation.supplierEmployee')}
                      disabled={!values.supplier?.id}
                      supplierId={values.supplier?.id}
                      state={[UserState.Active, UserState.Invited]}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                mt: '1rem',
                pt: '3rem',
                pb: '2rem',
                borderTop: values.mode === 'car' ? '1px solid #eee' : 'none',
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 20, mb: 1, display: 'block' }}
              >
                {t('visitation.selectInvitation')}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                }}
              >
                <Select fullWidth name="invitationId" label={t('tourniquet.invitation')} required={true} autoFocus={true}>
                  {invitationList.map((x) => (
                    <QcsMenuItem key={x.id} value={x.id}>
                      <SelectItem>
                        <FaIcon icon={faPersonWalkingArrowRight} />
                        <div>{getLangNameObj(i18n, x)}</div>
                      </SelectItem>
                    </QcsMenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          )}
          <Input css={{ margin: '1rem 0' }} name="note" label={t('common.notes')} multiline fullWidth rows={1} />
          <ShowError resource={[resources.visit, resources.readPin]} />
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            {values.supplierEmployee && <ShowPinBtn person={values.supplierEmployee} buttonType={'btn'} />}
            <SubmitButton text={t('visitation.saveEntry')} ignoreMarginLeft={true} css={{ flex: 1 }} />
          </Box>
        </Box>
      </FormContainer>
    </Form>
  );
};

export const VisitationAddModalPin: FC<Props> = ({ open, onClose, onOneTimeClick }) => {
  const { t } = useTranslation();
  const [invitationList, setInvitationList] = useState<InvitationDto[]>([]);
  const [hasValidCar, setHasValidCar] = useState(false);
  const [validSuppliers, setValidSuppliers] = useState<SupplierDto[] | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setInvitationList([]);
    setHasValidCar(false);
    setValidSuppliers(null);
  }, [open]);

  const finishVisit = () => {
    dispatch(visitationListActions.reload(true));
    dispatch(invitationTypeVisitListActions.reload(true));
    onClose();
  };

  const handleSubmit = async (data: VisitationAddPinForm, formikHelpers: FormikHelpers<VisitationAddPinForm>) => {
    // Determine type based on what's filled
    const type = data.supplierEmployee ? 'person' : 'pin';

    //Multiple active visitation - second Form.
    if (invitationList.length !== 0) {
      const entry: VisitationEntryDto = {
        invitation: data.invitationId,
        pin: data.pin || '',
        spz: data.spz,
        entryEstablishment: data.establishment,
        note: data.note,
      };
      await visitationService.visit(entry);

      finishVisit();
      return;
    }

    let pin = data.pin;
    if (type === 'person') {
      const revealPinRes = await supplierEmployeeService.revealPin(data.supplierEmployee!.id!);
      pin = revealPinRes.data.pin!;
    }

    const res = await visitationService.readPin({ pin: pin!, entryEstablishment: data.establishment }, true);

    //The visitor has not left yet, so leaving is the only action they may take.
    if (res.data.visitation) {
      const openInvitationId = res.data.visitation.invitation?.id;
      if (!openInvitationId) {
        apiMessagesStore.setError(resources.readPin, 'errorCodes.069');
        return;
      }

      await visitationService.visit({ invitation: openInvitationId, pin: pin! });
      finishVisit();
      return;
    }

    const invitations = res.data.invitations;

    //Only one invitation to enter on, no need to ask which.
    if (invitations.length === 1) {
      const entry: VisitationEntryDto = {
        invitation: invitations[0].id!,
        pin: pin!,
        spz: data.spz,
        entryEstablishment: data.establishment,
        note: data.note,
      };

      await visitationService.visit(entry);
      finishVisit();
      return;
    }

    //Multiple invitations to enter on - let the user pick in the second Form.
    setInvitationList(invitations);
    formikHelpers.setFieldValue('invitationId', invitations[0].id);

    if (type === 'person') {
      formikHelpers.setFieldValue('pin', pin);
    }
  };

  return (
    <>
      <SectionHeader title="visitation.add" />
      <VisitationFormContext.Provider value={{ hasValidCar, setHasValidCar, validSuppliers, setValidSuppliers }}>
        <FormikDebounce<VisitationAddPinForm>
          initialValues={{
            mode: 'walk',
            type: 'pin',
            invitationId: '',
            spz: '',
            pin: '',
            supplier: undefined,
            supplierEmployee: undefined,
            establishment: undefined,
          }}
          validationSchema={Yup.object({
            establishment: Yup.string().required(),
            spz: Yup.string().when('mode', {
              is: (mode: string) => mode === 'car',
              then: () => validations.stringRequired(t),
            }),
            pin: Yup.string().test('pin-or-employee', t('visitation.pinOrEmployeeRequired'), function (value) {
              const { supplierEmployee } = this.parent;
              // At least one of pin or supplierEmployee must be filled
              return !!value || !!supplierEmployee;
            }),
            supplier: Yup.object()
              .nullable()
              .when('supplierEmployee', {
                is: (supplierEmployee: any) => !!supplierEmployee,
                then: () => validations.objectRequired(t),
              }),
            supplierEmployee: Yup.object().nullable(),
            invitationId: invitationList.length === 0 ? Yup.string() : validations.stringRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          <VisitationFormContent invitationList={invitationList} />
        </FormikDebounce>

        {invitationList.length === 0 && (
          <QcsButton variant="outlined" fullWidth={true} sx={{ mt: '1rem' }} onClick={onOneTimeClick}>
            {t('visitation.oneTime')}
          </QcsButton>
        )}
      </VisitationFormContext.Provider>
    </>
  );
};
